import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, ReplaySubject, Subscription } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LanguageService implements OnDestroy {
  private readonly storageKey = 'language';
  private readonly languageSubject = new ReplaySubject<string>(1);
  private languageSubscription: Subscription;
  readonly language$ = this.languageSubject.asObservable();
  readonly languages = ['es', 'en'];

  constructor(private translate: TranslateService, private zone: NgZone,
    @Inject(DOCUMENT) private document: Document) { }

  get currentLanguage(): string {
    return this.translate.currentLang || 'es';
  }

  async initialize(): Promise<void> {
    this.translate.addLangs(this.languages);
    // Load before rendering: instant() must also work on a direct URL/reload.
    await forkJoin(this.languages.map(lang => this.translate.getTranslation(lang))).toPromise();
    this.translate.setDefaultLang('es');
    this.languageSubscription = this.translate.onLangChange.subscribe(event => {
      this.document.documentElement.lang = event.lang;
      this.languageSubject.next(event.lang);
    });
    await this.translate.use(this.readLanguage()).toPromise();
    window.addEventListener('storage', this.onStorageChange);
  }

  useLanguage(lang: string): void {
    if (!this.languages.includes(lang)) return;
    try {
      localStorage.setItem(this.storageKey, lang);
    } catch {
      // Storage may be blocked; the current session can still change language.
    }
    if (lang !== this.translate.currentLang) this.translate.use(lang);
  }

  private readLanguage(): string {
    try {
      const saved = localStorage.getItem(this.storageKey);
      return this.languages.includes(saved) ? saved : 'es';
    } catch {
      return 'es';
    }
  }

  private onStorageChange = (event: StorageEvent): void => {
    if (event.key === this.storageKey && event.storageArea === localStorage &&
        this.languages.includes(event.newValue) && event.newValue !== this.currentLanguage) {
      // Other open tabs receive this event; do not write it back to storage.
      this.zone.run(() => this.translate.use(event.newValue));
    }
  };

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.onStorageChange);
    if (this.languageSubscription) this.languageSubscription.unsubscribe();
    this.languageSubject.complete();
  }
}
